import {
  AnchorElement,
  rem,
  reset,
  radius,
  spacing,
  token,
  watch,
} from '@thiememeulenhoff/lit-sdk';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { tagName } from '../../../helpers';
import { PanelComponent } from './panel.component';

const tag = tagName('tab');

@customElement(tag)
export class TabComponent extends AnchorElement {
  static override styles = [
    reset.button,

    css`
      :where(span.stripe) {
        background: transparent;
        block-size: ${rem(4)};
        border-radius: ${radius('medium')};
        inset-block-end: ${rem(-1)};
        inset-inline: 0;
        position: absolute;
      }

      :where(label) {
        align-items: center;
        cursor: pointer;
        display: inline-flex;
        letter-spacing: 0.05em;
        padding: ${spacing(75)} 0;
        position: relative;
      }

      a {
        color: inherit;
        text-decoration: none;
      }

      a:focus-visible {
        outline-color: var(--tm-focus-ring-color);
        outline-offset: var(--tm-focus-ring-offset);
        outline-style: var(--tm-focus-ring-style);
        outline-width: var(--tm-focus-ring-width);
      }

      :host([selected]) {
        font-weight: bold;

        button {
          font-weight: bold;
        }
      }

      :host([variant='default'][selected]) {
        span.stripe {
          background: ${token('status-info-border')};
        }
      }

      :host([variant='default']) {
        :where(label) {
          &:hover span.stripe {
            background: ${token('status-info-foreground-hover')};
          }
        }
      }

      :host([variant='header']) {
        button {
          align-items: center;
          color: ${token('text-secondary')};
          column-gap: ${spacing(50)};
          display: flex;
        }

        label {
          padding: 0;
        }
      }

      :host([variant='filter'][selected]),
      :host([variant='header'][selected]) {
        button {
          color: ${token('foreground-selected')};
        }
      }

      :host([variant='filter']),
      :host([variant='header']) {
        button:hover {
          color: ${token('status-info-foreground-hover')};
        }

        span.stripe {
          display: none;
        }
      }

      :host([variant='filter'][disabled]),
      :host([variant='header'][disabled]) {
        label,
        button {
          color: ${token('foreground-disabled')};
          cursor: not-allowed;
        }

        :where(button) {
          font-size: ${rem(16)};
        }
      }

      :host([variant='header'][disabled]) {
        label,
        button {
          color: ${token('foreground-disabled')};
        }
      }
    `,
  ];

  @property({ type: String, reflect: true })
  variant?: 'default' | 'filter' | 'header' = 'default';

  @property({ type: String, reflect: true })
  readonly for!: string;

  @property({ type: Boolean, reflect: true, attribute: 'selected' })
  @state()
  selectedInternal = false;

  @property({ type: Boolean, reflect: true })
  override disabled = false;

  readonly #panels: Array<PanelComponent> = [];

  get selected(): boolean {
    return this.selectedInternal;
  }

  set selected(value: boolean) {
    const oldValue = this.selectedInternal;
    this.selectedInternal = value;
    this.requestUpdate('selected', oldValue);

    if (!value) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent('selected', {
        bubbles: true,
        composed: true,
        detail: { selected: this.selectedInternal, for: this.for },
      }),
    );
  }

  override render() {
    let slot = html`<button
      tabindex=${this.disabled ? -1 : 0}
      ?disabled=${this.disabled}
      @click=${() => this.setSelected(true)}
      data-test="tab-button"
    >
      <slot></slot>
    </button>`;

    if (this.href) {
      slot = html`<a
        part="tab"
        class="tab"
        href=${this.disabled ? nothing : this.href}
        target=${this.target ?? nothing}
        ?disabled=${this.disabled ? true : nothing}
        data-test="anchor"
        @click=${this.disabled ? nothing : () => this.setSelected(true)}
      >
        <slot></slot>
      </a>`;
    }

    return html`<label part="tab">
      ${slot}
      <span class="stripe"></span>
    </label>`;
  }

  public setSelected(selected: boolean) {
    if (this.selected === selected) {
      return;
    }

    this.selected = selected;
    this.#onSelectedChange();
  }

  @watch('selectedInternal', { waitUntilFirstUpdate: true })
  watchSelected(oldValue: unknown, newValue: unknown) {
    this.selected = newValue as boolean;
  }

  public addPanel(panel: PanelComponent) {
    if (!this.#panels.includes(panel)) {
      this.#panels.push(panel);
    }

    panel.setVisible(this.selected);
  }

  #onSelectedChange() {
    this.#panels.forEach((panel) => {
      panel.setVisible(this.selected);
    });
  }

  override connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'tab');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-tab': TabComponent;
  }
}
